import axios from 'axios';
import { getToken } from './auth';

import { API_URL } from './config';
const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

// 📦 Stock Lists
export const getStockLists = async (branchId, startDate, endDate, params = {}) =>
  (await axios.get(
    `${API_URL}/branch/${branchId}/stock-list`,
    {
      ...withAuth(),
      params: {
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...params,
      },
    }
  )).data;

export const getStockList = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/${id}`, withAuth())).data;

export const createStockList = async (branchId, data) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list`, data, withAuth())).data;

export const updateStockListActivationSchedule = async (branchId, id, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/stock-list/${id}/activation-schedule`, data, withAuth())).data;

export const deleteStockList = async (branchId, id) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/${id}`, withAuth())).data;

export const getExpensesByStockList = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/${id}/expense`, withAuth())).data;

export const finalizeStockList = async (branchId, id) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list/${id}/finalize`, {}, withAuth())).data;

export const runStockListPriceRecommendations = async (branchId, id) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list/${id}/price-recommendations/run`, {}, withAuth())).data;

export const getStockListPriceRecommendationRuns = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/${id}/price-recommendations/runs`, withAuth())).data;

export const getImportProfiles = async (branchId, varietyIds = []) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/import/profiles`, {
    ...withAuth(),
    params: { varietyIds: Array.isArray(varietyIds) ? varietyIds.join(',') : varietyIds },
  })).data;

export const upsertImportProfile = async (branchId, varietyId, data) =>
  (await axios.put(`${API_URL}/branch/${branchId}/stock-list/import/profiles/${varietyId}`, data, withAuth())).data;

export const getImportConversions = async (branchId, varietyIds = []) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/import/conversions`, {
    ...withAuth(),
    params: { varietyIds: Array.isArray(varietyIds) ? varietyIds.join(',') : varietyIds },
  })).data;

export const upsertImportConversions = async (branchId, data) =>
  (await axios.put(`${API_URL}/branch/${branchId}/stock-list/import/conversions`, data, withAuth())).data;

export const deleteImportConversion = async (branchId, id) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/import/conversions/${id}`, withAuth())).data;

// 📈 Stock Items
export const getLastStockItemsAfterPriceUpdate = async (branchId, params = {}) =>
  (await axios.get(
    `${API_URL}/branch/${branchId}/stock-list/stock/last-after-price-update`,
    { ...withAuth(), params }
  )).data;

export const getLastStockItemForEachVariety = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/stock/last`, { ...withAuth(), params })).data;

export const getEstimateUnitEquivalences = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/estimate/unit-equivalences`, withAuth())).data;

export const upsertEstimateUnitEquivalence = async (branchId, payload) =>
  (await axios.put(`${API_URL}/branch/${branchId}/stock-list/estimate/unit-equivalences`, payload || {}, withAuth())).data;

export const deleteEstimateUnitEquivalence = async (branchId, equivalenceId) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/estimate/unit-equivalences/${equivalenceId}`, withAuth())).data;

export const getStockEstimateConfirmations = async (branchId, stockListId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/${stockListId}/estimate/confirmations`, withAuth())).data;

export const confirmStockEstimateItem = async (branchId, stockListId, stockId) =>
  (await axios.post(
    `${API_URL}/branch/${branchId}/stock-list/${stockListId}/estimate/confirmations`,
    { stockId },
    withAuth()
  )).data;

export const unconfirmStockEstimateItem = async (branchId, stockListId, stockId) =>
  (await axios.delete(
    `${API_URL}/branch/${branchId}/stock-list/${stockListId}/estimate/confirmations/${stockId}`,
    withAuth()
  )).data;

export const createStockItem = async (branchId, data) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list/stock`, data, withAuth())).data;

export const getStock = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/stock/${id}`, withAuth())).data;

export const updateStock = async (branchId, id, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/stock-list/stock/${id}`, data, withAuth())).data;

export const deleteStock = async (branchId, id) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/stock/${id}`, withAuth())).data;

// 💳 Stock Payments
export const addStockPayment = async (branchId, stockId, data) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list/stock/${stockId}/payments`, data, withAuth())).data;

export const updateStockPayment = async (branchId, paymentId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/stock-list/stock/payments/${paymentId}`, data, withAuth())).data;

export const deleteStockPayment = async (branchId, paymentId) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/stock/payments/${paymentId}`, withAuth())).data;

// 💸 Expenses
export const createExpense = async (branchId, data) =>
  (await axios.post(`${API_URL}/branch/${branchId}/stock-list/expense`, data, withAuth())).data;

export const getExpense = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/stock-list/expense/${id}`, withAuth())).data;

export const updateExpense = async (branchId, id, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/stock-list/expense/${id}`, data, withAuth())).data;

export const deleteExpense = async (branchId, id) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/stock-list/expense/${id}`, withAuth())).data;
